import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Chip,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import ScheduleIcon from '@mui/icons-material/Schedule';
import { httpService } from '../../services/httpService';
import { themeBackgrounds, lightColors } from '../../theme/theme';
import { decryptResponse, encryptResponse, getSessionKey, REQ_ENC_KEY } from '../../services';

interface Payment {
  _id: string;
  receipt_id: string;
  plan_name: string;
  currency: string;
  status: string;
  amount: number;
  created_at: string;
  error_data?: any;
}

interface PaymentHistoryResponse {
  status: number;
  message: string;
  data: {
    payments: Payment[];
    pagination: {
      currentPage: number;
      totalPages: number;
      totalRecords: number;
      limit: number;
    };
  };
  error: any;
}

const PaymentHistory: React.FC = () => {
  const [payments, setPayments] = useState<Payment[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);

  const organizationId = useSelector((state: any) => state.auth.userDetails?.organization_id);

  const fetchPaymentHistory = async (page: number = 1, append: boolean = false) => {
    try {
      if (!append) {
        setLoading(true);
      } else {
        setLoadingMore(true);
      }

      const payload = {
        data: await encryptResponse({
          organization_id: organizationId,
          page: page,
          limit: 10,
        }, REQ_ENC_KEY),
      };
      const response = await httpService.post<any>('/user/getPaymentHistory', payload);
      if (response.data.status === 200 && response.data.data) {
        const data = response.data.data;
        const decryptedData : any = await decryptResponse(data, getSessionKey() as string);
        const newPayments = decryptedData.payments;
        const pagination = decryptedData.pagination;

        if (append) {
          setPayments(prev => [...prev, ...newPayments]);
        } else {
          setPayments(newPayments);
        }

        setHasMore(pagination.currentPage < pagination.totalPages);
        setCurrentPage(pagination.currentPage);
      } else {
        setError(response.data.message || 'Failed to fetch payment history');
      }
    } catch (err: any) {
      setError(err.message || 'An error occurred');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    if (!organizationId) return;

    fetchPaymentHistory(1, false);
  }, [organizationId]);

  const handleScroll = (event: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;
    const isNearBottom = scrollTop + clientHeight >= scrollHeight - 100;

    if (isNearBottom && hasMore && !loadingMore && !loading) {
      fetchPaymentHistory(currentPage + 1, true);
    }
  };

  const getStatusChip = (status: string) => {
    switch (status.toLowerCase()) {
      case 'success':
        return (
          <Chip
            icon={<CheckCircleIcon />}
            label="Success"
            sx={{
              background: `linear-gradient(135deg, ${lightColors[2]} 0%, #4caf50 100%)`, // Light Green to Green
              color: '#2e7d32',
              fontWeight: 'bold',
              '& .MuiChip-icon': { color: '#2e7d32' }
            }}
            size="small"
          />
        );
      case 'failed':
        return (
          <Chip
            icon={<ErrorIcon />}
            label="Failed"
            sx={{
              background: `linear-gradient(135deg, ${lightColors[0]} 0%, #f44336 100%)`, // Light Pink to Red
              color: '#d32f2f',
              fontWeight: 'bold',
              '& .MuiChip-icon': { color: '#d32f2f' }
            }}
            size="small"
          />
        );
      default:
        return (
          <Chip
            icon={<ScheduleIcon />}
            label={status}
            sx={{
              background: `linear-gradient(135deg, ${lightColors[3]} 0%, #ff9800 100%)`, // Light Yellow to Orange
              color: '#f57c00',
              fontWeight: 'bold',
              '& .MuiChip-icon': { color: '#f57c00' }
            }}
            size="small"
          />
        );
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress sx={{ color: '#007aff' }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert 
        severity="error" 
        sx={{ 
          borderRadius: 2,
          background: `linear-gradient(135deg, ${lightColors[0]} 0%, #ffebee 100%)`,
          color: '#c62828',
          fontWeight: 'bold',
          border: '1px solid #ffcdd2'
        }}
      >
        {error}
      </Alert>
    );
  }

  return (
    <Card
      sx={{
        background: '#ffffff',
        borderRadius: 3,
        boxShadow: '0 20px 40px rgba(0, 0, 0, 0.1)',
        border: '1px solid #e0e0e0',
        transition: 'transform 0.3s ease, box-shadow 0.3s ease',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: '0 30px 60px rgba(0, 0, 0, 0.15)',
        },
      }}
    >
      <CardContent sx={{ p: 4 }}>
        <Typography 
          variant="h5" 
          gutterBottom 
          sx={{ 
            fontWeight: 700,
            color: '#007aff',
            textAlign: 'center',
            mb: 3,
            textShadow: '0 2px 4px rgba(0, 122, 255, 0.3)',
          }}
        >
          Payment History
        </Typography>
        <TableContainer 
          component={Paper} 
          sx={{ 
            borderRadius: 2,
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
            background: 'rgba(255, 255, 255, 0.9)',
            backdropFilter: 'blur(10px)',
            maxHeight: 310,
            overflow: 'auto',
            '&::-webkit-scrollbar': {
              display: 'none',
            },
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
          }}
          onScroll={handleScroll}
        >
          <Table stickyHeader>
            <TableHead>
              <TableRow sx={{ background: themeBackgrounds.default }}>
                <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: '0.9rem', background: themeBackgrounds.default }}>Receipt ID</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: '0.9rem', background: themeBackgrounds.default }}>Plan Name</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: '0.9rem', background: themeBackgrounds.default }}>Amount</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: '0.9rem', background: themeBackgrounds.default }}>Currency</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: '0.9rem', background: themeBackgrounds.default }}>Status</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: '0.9rem', background: themeBackgrounds.default }}>Date</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {payments.map((payment) => (
                <TableRow 
                  key={payment._id}
                  sx={{
                    '&:hover': {
                      background: `linear-gradient(135deg, ${lightColors[1]} 0%, ${lightColors[7]} 100%)`, // Light Blue to Light Orange
                      transition: 'background 0.3s ease',
                    },
                  }}
                >
                  <TableCell sx={{ fontWeight: 500, color: '#1565c0' }}>{payment.receipt_id}</TableCell>
                  <TableCell sx={{ fontWeight: 500, color: '#1565c0' }}>{payment.plan_name}</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#007aff' }}>{payment.amount.toLocaleString()}</TableCell>
                  <TableCell sx={{ fontWeight: 500, color: '#424242' }}>{payment.currency}</TableCell>
                  <TableCell>{getStatusChip(payment.status)}</TableCell>
                  <TableCell sx={{ fontWeight: 500, color: '#424242' }}>
                    <Typography variant="body2" sx={{ fontSize: '0.8rem', color: '#666' }}>
                      {new Date(payment.created_at).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric'
                      })}, {(() => {
                        const date = new Date(payment.created_at);
                        const hours = date.getUTCHours();
                        const minutes = date.getUTCMinutes();
                        const displayHours = hours % 12 || 12;
                        const ampm = hours >= 12 ? 'PM' : 'AM';
                        return `${displayHours}:${minutes.toString().padStart(2, '0')} ${ampm}`;
                      })()}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
              {loadingMore && (
                <TableRow>
                  <TableCell colSpan={6} sx={{ textAlign: 'center', py: 2 }}>
                    <CircularProgress size={24} sx={{ color: '#007aff' }} />
                    <Typography variant="body2" sx={{ mt: 1, color: '#007aff' }}>
                      Loading more payments...
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {payments.length === 0 && !loading && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="body1" sx={{ color: '#007aff', fontStyle: 'italic', opacity: 0.7 }}>
              No payment history found
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default PaymentHistory;